import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import {
  Loader,
  RefreshCw,
  Droplets,
  Recycle,
  Truck,
  Shield,
  Play,
  Star,
  Quote,
  CheckCircle,
  ArrowRight,
  Clock,
  MapPin,
  PhoneCall,
  Zap,
  Filter,
  Sun,
  Sparkles,
} from "lucide-react";

const WaterService = () => {
  const [isMobile, setIsMobile] = useState(false);
  const [deliveryMessage, setDeliveryMessage] = useState("");

  // This would typically come from your app's configuration or API
  const isExpressDeliveryEnabled = false;

  const calculateDeliveryMessage = () => {
    const now = new Date();
    const colombiaTime = new Intl.DateTimeFormat("es-CO", {
      timeZone: "America/Bogota",
      hour: "numeric",
      hour12: false,
    }).format(now);

    const currentHour = parseInt(colombiaTime);

    // Express delivery logic
    if (isExpressDeliveryEnabled && currentHour >= 9 && currentHour < 16) {
      return "Pide ahora y recibe en menos de 90 minutos";
    }

    // Regular delivery logic
    if (currentHour >= 0 && currentHour <= 10) {
      return "Pide ahora y recibe por la tarde";
    } else if (currentHour > 10 && currentHour <= 16) {
      return "Pide ahora y recibe mañana por la mañana";
    } else {
      return "Pide ahora y recibe mañana por la tarde";
    }
  };

  useEffect(() => {
    const checkIfMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    checkIfMobile();
    window.addEventListener("resize", checkIfMobile);

    // Set initial delivery message
    setDeliveryMessage(calculateDeliveryMessage());

    // Update delivery message every minute
    const messageInterval = setInterval(() => {
      setDeliveryMessage(calculateDeliveryMessage());
    }, 60000);

    return () => {
      window.removeEventListener("resize", checkIfMobile);
      clearInterval(messageInterval);
    };
  }, []);

  const features = [
    {
      title: "19.5 lts de Agua Purificada",
      description: "Gran calidad y sabor refrescante.",
    },
    {
      title: "Purificación en 6 etapas",
      description: "Filtros especializados, Osmosis Inversa y UV.",
    },
    {
      title: "Botellón Retornable",
      description: "Te lo prestamos (no necesitas comprarlo).",
    },
  ];

  const benefits = [
    {
      title: "Recámbialo cuando se acabe",
      description: "Pide uno lleno y entrega el vacio. Por el mismo precio.",
    },
    {
      title: "Retornarlo cuando no lo uses ♻",
      description: "Cuando dures mas de 30 sin recambios nosotros lo buscamos.",
    },
    {
      title: "Domicilio programado gratis",
      description: "Lo entregamos donde lo necesites.",
    },
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.3,
      },
    },
  };

  const itemVariants = {
    hidden: {
      opacity: 0,
      y: 20,
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut",
      },
    },
  };

  const imageVariants = {
    hidden: {
      opacity: 0,
      scale: 0.8,
    },
    visible: {
      opacity: 1,
      scale: 1,
      transition: {
        duration: 0.8,
        ease: "easeOut",
      },
    },
  };

  const buttonVariants = {
    initial: { scale: 1 },
    hover: {
      scale: 1.05,
      transition: {
        duration: 0.2,
      },
    },
    tap: {
      scale: 0.95,
    },
  };

  const ContentBlock = ({ items, alignment = "start" }) => (
    <motion.div
      className="flex-1 flex flex-col justify-center items-start gap-8"
      variants={containerVariants}
      initial="hidden"
      animate="visible"
    >
      {items.map((item, index) => (
        <motion.div key={index} className="w-full" variants={itemVariants}>
          <div className="w-full bg-white rounded-2xl flex flex-col justify-center items-start gap-2">
            <div
              className={`text-neutral-800 text-xl font-medium leading-tight ${
                alignment === "end" ? "text-right w-full" : ""
              }`}
            >
              {item.title}
            </div>
            <div
              className={`text-neutral-400 text-base font-normal leading-none ${
                alignment === "end" ? "text-right w-full" : ""
              }`}
            >
              {item.description}
            </div>
          </div>
          {index < items.length - 1 && (
            <div className="w-full h-px bg-sky-100 mt-6" />
          )}
        </motion.div>
      ))}
    </motion.div>
  );

  const DeliveryInfo = () => (
    <div className="bg-sky-100 justify-start items-center gap-1 inline-flex">
      <div className="p-2 justify-center items-center gap-2.5 flex">
        <div className="text-center text-neutral-800 text-xl font-medium leading-tight">
          {deliveryMessage}
        </div>
      </div>
    </div>
  );

  return (
    <div className="bg-white py-8 px-8 rounded-3xl max-w-6xl w-full">
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-16">
          <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4 fraunces ">
            Botellón PET Premium
          </h2>
          <p className="text-lg text-gray-600 max-w-3xl mx-auto">
            Agua purificada premium en botellón PET sin BPA, procesada en 6
            etapas para máxima pureza y sabor, pensada para tu salud,
            conveniencia y el planeta.
          </p>
        </div>
        {/* Layout Desktop - Imagen central con líneas */}
        <div className="hidden lg:block relative">
          <div className="flex justify-center items-center">
            {/* Imagen central del botellón */}
            <div className="w-80 h-96 relative z-10">
              <img
                src="/assets/img/landing/full-sug-drops-blue-plan-recambios-botellon-20l-dispensador-pedidos-online-entrega-domicilio-agua-purificada-calidad.png"
                alt="Botellón PET 20L"
                className="w-full h-full object-contain"
              />
            </div>

            {/* Beneficios con líneas apuntando - Lado izquierdo */}
            <div className="absolute left-0 top-1/2 transform -translate-y-1/2 space-y-12">
              {/* 100% libre de BPA */}
              <div className="relative">
                <div className="bg-stone-50 rounded-xl p-4  max-w-xs">
                  <div className="flex items-center gap-3">
                    <Shield className="w-6 h-6 text-green-500" />
                    <span className="font-medium text-gray-900">
                      100% libre de BPA
                    </span>
                  </div>
                </div>
                <svg
                  className="absolute left-full top-1/2 transform -translate-y-1/2"
                  width="100"
                  height="2"
                >
                  <line
                    x1="0"
                    y1="1"
                    x2="100"
                    y2="1"
                    stroke="#e5e7eb"
                    strokeWidth="2"
                    strokeDasharray="5,5"
                  />
                </svg>
              </div>

              {/* Material PET reciclable */}
              <div className="relative">
                <div className="bg-stone-50 rounded-xl p-4  max-w-xs">
                  <div className="flex items-center gap-3">
                    <Recycle className="w-6 h-6 text-green-500" />
                    <span className="font-medium text-gray-900">
                      Material PET reciclable
                    </span>
                  </div>
                </div>
                <svg
                  className="absolute left-full top-1/2 transform -translate-y-1/2"
                  width="100"
                  height="2"
                >
                  <line
                    x1="0"
                    y1="1"
                    x2="100"
                    y2="1"
                    stroke="#e5e7eb"
                    strokeWidth="2"
                    strokeDasharray="5,5"
                  />
                </svg>
              </div>

              {/* Apto uso alimentario */}
              <div className="relative">
                <div className="bg-stone-50 rounded-xl p-4  max-w-xs">
                  <div className="flex items-center gap-3">
                    <CheckCircle className="w-6 h-6 text-green-500" />
                    <span className="font-medium text-gray-900">
                      Apto uso alimentario
                    </span>
                  </div>
                </div>
                <svg
                  className="absolute left-full top-1/2 transform -translate-y-1/2"
                  width="100"
                  height="2"
                >
                  <line
                    x1="0"
                    y1="1"
                    x2="100"
                    y2="1"
                    stroke="#e5e7eb"
                    strokeWidth="2"
                    strokeDasharray="5,5"
                  />
                </svg>
              </div>

              {/* Conserva pureza del agua */}
              <div className="relative">
                <div className="bg-stone-50 rounded-xl p-4  max-w-xs">
                  <div className="flex items-center gap-3">
                    <Droplets className="w-6 h-6 text-blue-500" />
                    <span className="font-medium text-gray-900">
                      Conserva pureza del agua
                    </span>
                  </div>
                </div>
                <svg
                  className="absolute left-full top-1/2 transform -translate-y-1/2"
                  width="100"
                  height="2"
                >
                  <line
                    x1="0"
                    y1="1"
                    x2="100"
                    y2="1"
                    stroke="#e5e7eb"
                    strokeWidth="2"
                    strokeDasharray="5,5"
                  />
                </svg>
              </div>
            </div>

            {/* Beneficios con líneas apuntando - Lado derecho */}
            <div className="absolute right-0 top-1/2 transform -translate-y-1/2 space-y-12">
              {/* Entrega programada */}
              <div className="relative">
                <svg
                  className="absolute right-full top-1/2 transform -translate-y-1/2"
                  width="100"
                  height="2"
                >
                  <line
                    x1="0"
                    y1="1"
                    x2="100"
                    y2="1"
                    stroke="#e5e7eb"
                    strokeWidth="2"
                    strokeDasharray="5,5"
                  />
                </svg>
                <div className="bg-stone-50 rounded-xl p-4  max-w-xs">
                  <div className="flex items-center gap-3">
                    <Clock className="w-6 h-6 text-blue-500" />
                    <span className="font-medium text-gray-900">
                      Entrega programada &lt; 24h
                    </span>
                  </div>
                </div>
              </div>

              {/* Sin cargar peso */}
              <div className="relative">
                <svg
                  className="absolute right-full top-1/2 transform -translate-y-1/2"
                  width="100"
                  height="2"
                >
                  <line
                    x1="0"
                    y1="1"
                    x2="100"
                    y2="1"
                    stroke="#e5e7eb"
                    strokeWidth="2"
                    strokeDasharray="5,5"
                  />
                </svg>
                <div className="bg-stone-50 rounded-xl p-4  max-w-xs">
                  <div className="flex items-center gap-3">
                    <Truck className="w-6 h-6 text-blue-500" />
                    <span className="font-medium text-gray-900">
                      Sin cargar peso
                    </span>
                  </div>
                </div>
              </div>

              {/* Sistema de recambio */}
              <div className="relative">
                <svg
                  className="absolute right-full top-1/2 transform -translate-y-1/2"
                  width="100"
                  height="2"
                >
                  <line
                    x1="0"
                    y1="1"
                    x2="100"
                    y2="1"
                    stroke="#e5e7eb"
                    strokeWidth="2"
                    strokeDasharray="5,5"
                  />
                </svg>
                <div className="bg-stone-50 rounded-xl p-4  max-w-xs">
                  <div className="flex items-center gap-3">
                    <RefreshCw className="w-6 h-6 text-green-500" />
                    <span className="font-medium text-gray-900">
                      Recámbialo al terminarse
                    </span>
                  </div>
                </div>
              </div>

              {/* 6 etapas certificadas */}
              <div className="relative">
                <svg
                  className="absolute right-full top-1/2 transform -translate-y-1/2"
                  width="100"
                  height="2"
                >
                  <line
                    x1="0"
                    y1="1"
                    x2="100"
                    y2="1"
                    stroke="#e5e7eb"
                    strokeWidth="2"
                    strokeDasharray="5,5"
                  />
                </svg>
                <div className="bg-stone-50 rounded-xl p-4  max-w-xs">
                  <div className="flex items-center gap-3">
                    <Filter className="w-6 h-6 text-blue-500" />
                    <span className="font-medium text-gray-900">
                      6 etapas de purificación
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Layout Mobile - Lista simple */}
        <div className="lg:hidden">
          <div className="flex justify-center mb-8">
            <div className="w-48 h-64">
              <img
                src="/assets/img/landing/full-sug-drops-blue-plan-recambios-botellon-20l-dispensador-pedidos-online-entrega-domicilio-agua-purificada-calidad.png"
                alt="Botellón PET 20L"
                className="w-full h-full object-contain"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 gap-4">
            <div className="bg-white rounded-xl p-4 flex items-center gap-3">
              <Shield className="w-6 h-6 text-green-500 flex-shrink-0" />
              <span className="font-medium text-gray-900">
                100% libre de BPA
              </span>
            </div>
            <div className="bg-white rounded-xl p-4 flex items-center gap-3">
              <Recycle className="w-6 h-6 text-green-500 flex-shrink-0" />
              <span className="font-medium text-gray-900">
                Material PET reciclable
              </span>
            </div>
            <div className="bg-white rounded-xl p-4 flex items-center gap-3">
              <CheckCircle className="w-6 h-6 text-green-500 flex-shrink-0" />
              <span className="font-medium text-gray-900">
                Apto uso alimentario
              </span>
            </div>
            <div className="bg-white rounded-xl p-4 flex items-center gap-3">
              <Droplets className="w-6 h-6 text-blue-500 flex-shrink-0" />
              <span className="font-medium text-gray-900">
                Conserva pureza del agua
              </span>
            </div>
            <div className="bg-white rounded-xl p-4 flex items-center gap-3">
              <Clock className="w-6 h-6 text-blue-500 flex-shrink-0" />
              <span className="font-medium text-gray-900">
                Entrega programada en menos de 24h
              </span>
            </div>
            <div className="bg-white rounded-xl p-4 flex items-center gap-3">
              <Truck className="w-6 h-6 text-blue-500 flex-shrink-0" />
              <span cla ssName="font-medium text-gray-900">
                Sin cargar peso: nosotros llevamos y recogemos
              </span>
            </div>
            <div className="bg-white rounded-xl p-4 flex items-center gap-3">
              <RefreshCw className="w-6 h-6 text-green-500 flex-shrink-0" />
              <span className="font-medium text-gray-900">
                Recámbialo al terminarse
              </span>
            </div>
            <div className="bg-white rounded-xl p-4 flex items-center gap-3">
              <Filter className="w-6 h-6 text-blue-500 flex-shrink-0" />
              <span className="font-medium text-gray-900">
                Purificación de 6 etapas 
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WaterService;
